import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
    ResponsiveContainer,
    Tooltip,
    Legend,
    PieChart,
    Pie,
    Cell,
    Curve,
} from 'recharts';
import PieChartTooltip from './PieChartTooltip';
import {
    colors,
    minPieLabelDisplayPercentage,
} from '../../../../shared/constants/constants';
import { getFormattedValue } from '../../../dashboardUtils';
import { lightenOrDarkenColor } from '../../widgetMenuUtils';

/* eslint-disable react/no-unstable-nested-components */
function GaugePieChart({
    actions,
    dataPoints,
    chartProperties,
    legendFormatter,
    metricName,
    metricValueKey,
    onContextMenuOpen,
}) {
    const [activeIndex, setActiveIndex] = useState(-1);

    const {
        colorPalette = colors,
        legendProperties,
        pieChartProperties,
        pieProperties,
        responsiveContainerProperties,
        showLegend,
        tooltipProperties,
        suffix,
    } = chartProperties;

    const labelLine = useCallback(
        (data) =>
            data.percent >= minPieLabelDisplayPercentage ? (
                <Curve
                    {...data}
                    type="linear"
                    className="recharts-pie-label-line"
                />
            ) : null,
        []
    );

    function getCellColor(index) {
        const color = colorPalette[index % colorPalette.length];
        return actions && index === activeIndex
            ? lightenOrDarkenColor(color, -10)
            : color;
    }

    function renderCells() {
        return dataPoints.map((dataPoint, index) => (
            <Cell
                key={dataPoint[metricName]}
                fill={getCellColor(index)}
                style={{ cursor: actions ? 'pointer' : 'auto' }}
                onMouseOver={() => setActiveIndex(index)}
                onMouseOut={() => setActiveIndex(-1)}
                onFocus={() => setActiveIndex(index)}
                onBlur={() => setActiveIndex(-1)}
            />
        ));
    }

    return (
        <ResponsiveContainer {...responsiveContainerProperties}>
            <PieChart {...pieChartProperties} margin={{ top: 20, bottom: 20 }}>
                <Tooltip
                    {...tooltipProperties}
                    cursor={dataPoints.length !== 0}
                    content={
                        <PieChartTooltip
                            activeIndex={activeIndex}
                            axisKey={metricName}
                            axisFormatter={legendFormatter}
                            suffix={suffix}
                        />
                    }
                />
                {showLegend && (
                    <Legend
                        {...legendProperties}
                        onMouseEnter={(data, index, element) => {
                            element.currentTarget.setAttribute(
                                'title',
                                `${legendFormatter(
                                    data.payload[metricName]
                                )}: ${data.payload.value}`
                            );
                        }}
                        formatter={(value, entry) =>
                            legendFormatter(entry.payload[metricName])
                        }
                    />
                )}
                <Pie
                    {...pieProperties}
                    data={dataPoints}
                    dataKey={metricValueKey}
                    label={(data) =>
                        data.percent >= minPieLabelDisplayPercentage
                            ? getFormattedValue(data.value, suffix)
                            : null
                    }
                    labelLine={labelLine}
                    onClick={(eventData, index, { currentTarget }) =>
                        onContextMenuOpen(eventData, currentTarget)
                    }
                >
                    {renderCells()}
                </Pie>
            </PieChart>
        </ResponsiveContainer>
    );
}

GaugePieChart.propTypes = {
    actions: PropTypes.instanceOf(Object),
    dataPoints: PropTypes.instanceOf(Array),
    chartProperties: PropTypes.instanceOf(Object).isRequired,
    legendFormatter: PropTypes.func.isRequired,
    metricName: PropTypes.string.isRequired,
    metricValueKey: PropTypes.string.isRequired,
    onContextMenuOpen: PropTypes.func.isRequired,
};

GaugePieChart.defaultProps = {
    dataPoints: [],
};

export default React.memo(
    GaugePieChart,
    (prevProps, nextProps) =>
        _.isEqual(prevProps.dataPoints, nextProps.dataPoints) &&
        _.isEqual(prevProps.WidgetActionMenu, nextProps.WidgetActionMenu)
);
